import { useState } from "react";
import { Link } from "wouter";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import apiService from "@/services/api.service";
import FooterPublic from "@/components/layout/footer-public";

export default function EsqueciSenha() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsLoading(true);

    let recaptchaToken = "";
    try {
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha("password_recovery");
      }
    } catch {
      // reCAPTCHA não disponível (dev/E2E)
    }

    await apiService.post(
      "/auth/password/recovery",
      { email: email.trim() },
      { skipAuth: true, headers: recaptchaToken ? { "x-recaptcha-token": recaptchaToken } : undefined },
    );
    setIsLoading(false);
    setSent(true);
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Left Side */}
      <div className="w-full lg:w-1/2 flex flex-col px-4 sm:px-8 md:px-16 lg:px-24 py-8 sm:py-12 lg:py-0 bg-white min-h-screen lg:min-h-0">
        <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex justify-start mb-6">
            <img
              src="/assets/login/image-logo.svg"
              alt="Liqi - Portal do cliente"
              className="h-12 sm:h-14 w-auto"
            />
          </div>

          {sent ? (
            <>
              {/* Success State */}
              <div className="mb-8">
                <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                  </svg>
                </div>
                <h1 style={{ fontSize: "30px", lineHeight: "36px" }} className="font-semibold text-neutral-900 mb-2">
                  Verifique seu email
                </h1>
                <p style={{ fontSize: "16px", lineHeight: "24px" }} className="text-neutral-500">
                  Se o email <strong className="text-neutral-700">{email}</strong> estiver cadastrado, enviaremos um link para redefinir sua senha.
                </p>
                <p style={{ fontSize: "14px", lineHeight: "20px" }} className="text-neutral-600 mt-3">
                  O link expira em 30 minutos. Verifique sua caixa de spam caso não encontre o email.
                </p>
              </div>

              <Link
                href="/login"
                className="inline-flex items-center gap-2 text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
                </svg>
                Voltar para o login
              </Link>
            </>
          ) : (
            <>
              {/* Form State */}
              <div className="mb-8">
                <h1 style={{ fontSize: "30px", lineHeight: "36px" }} className="font-semibold text-neutral-900 mb-2">
                  Esqueceu sua senha?
                </h1>
                <p style={{ fontSize: "16px", lineHeight: "24px" }} className="text-neutral-500">
                  Informe seu email e enviaremos um link para redefinir sua senha.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="email" className="text-sm font-medium text-neutral-900">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full py-3 px-4 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300 focus:border-transparent"
                    style={{ backgroundColor: "#ffffff", color: "#171717" }}
                    required
                    disabled={isLoading}
                    autoFocus
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 px-6 rounded-lg text-white font-semibold text-base transition-all disabled:opacity-50"
                  style={{ backgroundColor: "#0247FE" }}
                >
                  {isLoading ? "Enviando..." : "Enviar link de recuperação"}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  href="/login"
                  className="inline-flex items-center gap-2 text-sm font-medium text-neutral-500 hover:text-neutral-700 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
                  </svg>
                  Voltar para o login
                </Link>
              </div>
            </>
          )}
        </div>
        </div>
        <FooterPublic />
      </div>

      {/* Right Side - Branding Panel (dark) — same as login */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center p-8 lg:p-12 relative overflow-hidden" style={{ backgroundColor: "#111218" }}>
        <div className="relative z-10 max-w-2xl mx-auto w-full flex flex-col justify-between min-h-[60vh]">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-12">
              <img src="/assets/login/login-logo-white.svg" alt="Liqi" className="h-8 lg:h-10 w-auto" />
            </div>
            <div className="mb-12">
              <h2 style={{ fontSize: "36px", lineHeight: "44px" }} className="font-semibold text-white mb-4">
                Governança completa para
                <br />
                suas operações
              </h2>
              <p style={{ fontSize: "16px", lineHeight: "28px" }} className="text-white/60 max-w-md mx-auto">
                Gerencie vencimentos, pagamentos, projeções
                de caixa e análises com inteligência artificial.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
